// eslint-disable-next-line import/order
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import "./Pagination.scss";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import { useTheme } from "@mui/material/styles";
import { KeyboardEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface PaginationProps {
  count: number;
  lastVisitedPage?: number;
  onChange: any;
}

enum PageNavigationDirection {
  FORWARD,
  BACKWARD,
}

const Pagination = (props: PaginationProps) => {
  const { count, onChange, lastVisitedPage } = props;
  const [page, setPage] = useState<number>(lastVisitedPage ?? 1);
  const [pageInput, setPageInput] = useState<number>(page);
  const theme = useTheme();
  const { t } = useTranslation("common");

  useEffect(() => {
    const updatedPage = lastVisitedPage ?? 1;
    setPageInput(updatedPage);
    setPage(updatedPage);
    onChange(updatedPage);
  }, [count]);

  const handleOnePageNavigation = (
    pageNavigationDirection: PageNavigationDirection
  ) => {
    if (pageNavigationDirection === PageNavigationDirection.FORWARD) {
      setPage(page + 1);
      setPageInput(page + 1);
      onChange(page + 1);
    } else {
      setPage(page - 1);
      setPageInput(page - 1);
      onChange(page - 1);
    }
  };

  const handleGoToPage = () => {
    if (pageInput > 0 && pageInput <= count) {
      onChange(pageInput);
      setPage(pageInput);
    } else {
      onChange(page);
      setPageInput(page);
    }
  };

  const handleEnterPressOnInput = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      handleGoToPage();
      if (event.target instanceof HTMLElement) event.target.blur();
    }
  };

  const spinnerSx = {
    WebkitAppearance: "none",
    MozAppearance: "none",
    appearance: "none",
    margin: 0,
  };

  return (
    <Box
      data-testid="table-pagination"
      sx={{
        display: "flex",
        alignItems: "center",
        "input[type=number]::-webkit-outer-spin-button": {
          ...spinnerSx,
        },
        "input[type=number]::-webkit-inner-spin-button": {
          ...spinnerSx,
        },

        "input[type=number]": {
          MozAppearance: "textfield",
        },
      }}
    >
      <IconButton
        data-testid="page-backward-navigation-button"
        style={{ marginRight: theme.tokens.FuelSpacing3 }}
        data-fuel-outlined
        disabled={page <= 1}
        aria-label="Go to previous page"
        onClick={() =>
          handleOnePageNavigation(PageNavigationDirection.BACKWARD)
        }
      >
        <NavigateBefore />
      </IconButton>
      <TextField
        data-testid="page-number-text-field"
        sx={{
          width: "48px",
          marginRight: theme.tokens.FuelSpacing3,
          color: theme.tokens.FuelColorTextTertiary,
          "& .MuiInputBase-root.Mui-disabled": {
            backgroundColor: theme.tokens.FuelColorBackgroundWhite,
          },
        }}
        id="page-number"
        type="number"
        disabled={!count}
        value={count ? pageInput : 0}
        onChange={(event) => {
          setPageInput(parseInt(event.target.value, 10));
        }}
        onKeyPress={(event) => handleEnterPressOnInput(event)}
      />
      <Typography
        data-testid="page-count"
        sx={{
          color: theme.tokens.FuelColorTextTertiary,
          marginRight: theme.tokens.FuelSpacing3,
        }}
      >
        {t("table.pagination.results.of")} {count}
      </Typography>
      <Button
        data-testid="go-to-page-button"
        sx={{
          width: "fit-content",
          marginRight: theme.tokens.FuelSpacing3,
          padding: "6px 11.65px !important",
        }}
        variant="primary"
        disabled={!count}
        onClick={() => handleGoToPage()}
      >
        {t("table.pagination.results.go")}
      </Button>
      <IconButton
        data-testid="page-forward-navigation-button"
        data-fuel-outlined
        aria-label="Go to next page"
        disabled={page >= count}
        onClick={() => handleOnePageNavigation(PageNavigationDirection.FORWARD)}
      >
        <NavigateNext />
      </IconButton>
    </Box>
  );
};

export default Pagination;
